<template>
  <coozzy-card :title="$t('message.marketingSettings.affordability.title')">
    <b-card-sub-title>
      {{ $t('message.marketingSettings.affordability.subTitle') }}
    </b-card-sub-title>
    <div
      v-if="loading"
      class="row">
      <div class="col text-center">
        <coozzy-spinner />
      </div>
    </div>
    <template v-else>
      <div class="row mt-3 align-items-end">
        <div class="col-sm-6 col-md-3 mb-2">
          <coozzy-form-input-numeric
            v-model="maxAffordabilityPercent"
            :name="$t('message.marketingSettings.affordability.maxAffordability')"
            :check-valide="$v.maxAffordabilityPercent && $v.maxAffordabilityPercent.$dirty && $v.maxAffordabilityPercent.$error ? 'is-invalid' : ''"
            :is-integer="true"
            :is-positive="true"
            :limited="true"
            :is-read-only="readOnly"
            @blur="onMaxAffordabilityBlur" />
        </div>
        <div class="col-sm-6 col-md-3 mb-2">
          <coozzy-form-input-numeric
            v-model="tolerancePercent"
            :name="$t('message.marketingSettings.affordability.toleranceRange')"
            :check-valide="$v.tolerancePercent && $v.tolerancePercent.$dirty && $v.tolerancePercent.$error ? 'is-invalid' : ''"
            :is-integer="true"
            :is-read-only="readOnly"
            @blur="onToleranceBlur" />
        </div>
        <div class="col-md-6 mb-2">
          <label>{{ $t('message.marketingSettings.affordability.preview') }}</label>
          <affordability-preview-bar
            :max-affordability-percent="maxAffordabilityPercent"
            :tolerance-percent="tolerancePercent" />
        </div>
      </div>
      <div class="row">
        <div class="col">
          <coozzy-button
            v-if="isCompanyAdmin"
            design="primary"
            class="float-right"
            @click="saveClicked">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </div>
    </template>
  </coozzy-card>
</template>

<script>
import { required, integer, between } from 'vuelidate/lib/validators'
import CoozzyCard from '@/framework/components/card/CoozzyCard'
import CoozzyFormInputNumeric from '@/framework/components/form/input/CoozzyFormInputNumeric'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import AffordabilityPreviewBar from '@/settings/marketing/AffordabilityPreviewBar.vue'
import { user } from '@/mixins/user'
import Vue from 'vue'
import { loadReferenceCheckSettings, saveReferenceCheckSettings } from '@/settings/marketing/referenceCheckSettings'

const TOTAL_PERCENT = 100
const MAX_AFFORDABILITY_PERCENT = TOTAL_PERCENT - 1

export default {
  name: 'AffordabilitySettings',
  components: {
    AffordabilityPreviewBar,
    CoozzySpinner,
    CoozzyButton,
    CoozzyFormInputNumeric,
    CoozzyCard
  },
  mixins: [user],
  data() {
    return {
      loading: false,
      maxAffordabilityPercent: 33,
      tolerancePercent: 35
    }
  },
  computed: {
    readOnly() {
      return !this.isCompanyAdmin
    },
    minTolerancePercent() {
      const max = Number(this.maxAffordabilityPercent)
      const used = Number.isFinite(max) ? Math.min(Math.max(max, 0), MAX_AFFORDABILITY_PERCENT) : 0
      return used + 1
    }
  },
  watch: {
    maxAffordabilityPercent(value) {
      const parsed = Number(value)
      if (Number.isFinite(parsed) && parsed > MAX_AFFORDABILITY_PERCENT) {
        this.maxAffordabilityPercent = MAX_AFFORDABILITY_PERCENT
      }
    }
  },
  mounted() {
    this.loadSettings()
  },
  methods: {
    loadSettings() {
      this.loading = true
      loadReferenceCheckSettings(this.accountId, this.currentLanguage)
        .then(settings => {
          this.maxAffordabilityPercent = settings.maxAffordabilityPercent
          this.tolerancePercent = settings.tolerancePercent
        })
        .catch(() => {
          Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
        })
        .finally(() => {
          this.clampToleranceToValidRange()
          this.loading = false
        })
    },
    clampToleranceToValidRange() {
      const max = Number(this.maxAffordabilityPercent)
      if (Number.isFinite(max) && max > MAX_AFFORDABILITY_PERCENT) {
        this.maxAffordabilityPercent = MAX_AFFORDABILITY_PERCENT
      }
      const tolerance = Number(this.tolerancePercent)
      if (!Number.isFinite(tolerance)) {
        return
      }
      if (tolerance < this.minTolerancePercent) {
        this.tolerancePercent = this.minTolerancePercent
      } else if (tolerance > TOTAL_PERCENT) {
        this.tolerancePercent = TOTAL_PERCENT
      }
    },
    onMaxAffordabilityBlur() {
      this.clampToleranceToValidRange()
    },
    onToleranceBlur() {
      this.clampToleranceToValidRange()
    },
    saveClicked() {
      this.clampToleranceToValidRange()
      if (this.$v) {
        this.$v.$touch()
        if (this.$v.$invalid) {
          return
        }
      }

      this.loading = true
      saveReferenceCheckSettings(this.accountId, this.currentLanguage, {
        maxAffordabilityPercent: this.maxAffordabilityPercent,
        tolerancePercent: this.tolerancePercent
      }).then(() => {
        Vue.toasted.show(this.$t('message.marketingSettings.affordability.savedSuccessfully'), { type: 'success' })
      }).catch(() => {
        Vue.toasted.show(this.$t('message.marketingSettings.affordability.savedFailed'), { type: 'error' })
      }).finally(() => {
        this.loading = false
      })
    }
  },
  validations() {
    return {
      maxAffordabilityPercent: {
        required,
        integer,
        between: between(1, MAX_AFFORDABILITY_PERCENT)
      },
      tolerancePercent: {
        required,
        integer,
        between: between(this.minTolerancePercent, TOTAL_PERCENT)
      }
    }
  }
}
</script>
